"use client";

import { format } from "date-fns";
import { useRouter } from "next/navigation";
import { forwardRef, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { arCalendar } from "@/lib/datepicker-ar";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("ar", arCalendar); // one-letter weekday headers (fit phones)

export function Icon({
  name,
  className = "",
}: {
  name: string;
  className?: string;
}) {
  return (
    <span className={`material-symbols-outlined ${className}`}>{name}</span>
  );
}

export default function HeroSearch({
  locale,
  isAr,
}: {
  locale: string;
  isAr: boolean;
}) {
  const router = useRouter();
  const t = (a: string, e: string) => (isAr ? a : e);

  const [search, setSearch] = useState("");
  const [arrival, setArrival] = useState<Date | null>(null);
  const [departure, setDeparture] = useState<Date | null>(null);
  const [adults, setAdults] = useState(1);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    const arrivalStr = arrival ? format(arrival, "yyyy-MM-dd") : "";
    const departureStr = departure ? format(departure, "yyyy-MM-dd") : "";
    if (arrivalStr) params.set("arrival_date", arrivalStr);
    if (departureStr) params.set("departure_date", departureStr);
    if (adults) params.set("adults", String(adults));

    // Persist the stay search so it auto-fills on any property page.
    try {
      sessionStorage.setItem(
        "diriyah_stay_search",
        JSON.stringify({ arrival: arrivalStr, departure: departureStr, adults }),
      );
    } catch {
      /* ignore storage errors */
    }

    const q = params.toString();
    router.push(`/${locale}/explore${q ? `?${q}` : ""}`);
  };

  // Borderless button that opens the calendar — matches the pill fields.
  const DateInput = forwardRef<
    HTMLButtonElement,
    { value?: string; onClick?: () => void }
  >(({ value, onClick }, ref) => (
    <button
      type="button"
      ref={ref}
      onClick={onClick}
      className="flex w-full items-center gap-2 bg-transparent p-0 text-start text-[15px] text-ink-900 outline-none"
    >
      <Icon name="calendar_month" className="text-[18px] text-clay-600" />
      <span className={value ? "" : "text-ink-500/50"}>
        {value || t("أضف تاريخاً", "Add date")}
      </span>
    </button>
  ));
  DateInput.displayName = "DateInput";

  return (
    <form
      onSubmit={onSubmit}
      className="bg-surface h-auto md:h-19 w-full rounded-3xl md:rounded-full shadow-2xl flex flex-col md:flex-row items-center gap-2 md:gap-0 p-2.5 md:p-2 border border-clay-100"
    >
      <div className="flex-1 grid grid-cols-1 md:grid-cols-4 w-full gap-1 md:gap-0">
        {/* Destination */}
        <div className="px-4 md:px-6 py-2.5 md:py-2 border-b md:border-b-0 md:border-e border-clay-100 text-start">
          <label className="block text-[12px] text-ink-500 font-bold mb-0.5">
            {t("أين تريد أن تقيم؟", "Where to?")}
          </label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-transparent border-none p-0 text-[15px] text-ink-900 focus:ring-0 outline-none placeholder:text-ink-500/50"
            placeholder={t("جدة، الرياض، الطريف...", "Jeddah, Riyadh...")}
          />
        </div>

        {/* Check-in date */}
        <div className="px-4 md:px-6 py-2.5 md:py-2 border-b md:border-b-0 md:border-e border-clay-100 text-start">
          <label className="block text-[12px] text-ink-500 font-bold mb-0.5">
            {t("تاريخ الوصول", "Check-in")}
          </label>
          <DatePicker
            selected={arrival}
            onChange={(d: Date | null) => {
              setArrival(d);
              if (d && departure && departure <= d) setDeparture(null);
            }}
            minDate={new Date()}
            locale={isAr ? "ar" : undefined}
            dateFormat="dd MMM yyyy"
            customInput={<DateInput />}
            popperPlacement="bottom-start"
            calendarClassName="najdi-calendar"
            showPopperArrow={false}
          />
        </div>

        {/* Check-out date */}
        <div className="px-4 md:px-6 py-2.5 md:py-2 border-b md:border-b-0 md:border-e border-clay-100 text-start">
          <label className="block text-[12px] text-ink-500 font-bold mb-0.5">
            {t("تاريخ المغادرة", "Check-out")}
          </label>
          <DatePicker
            selected={departure}
            onChange={(d: Date | null) => setDeparture(d)}
            minDate={
              arrival
                ? new Date(arrival.getTime() + 86400000)
                : new Date(Date.now() + 86400000)
            }
            locale={isAr ? "ar" : undefined}
            dateFormat="dd MMM yyyy"
            customInput={<DateInput />}
            popperPlacement="bottom-start"
            calendarClassName="najdi-calendar"
            showPopperArrow={false}
          />
        </div>

        {/* Guests */}
        <div className="px-4 md:px-6 py-2.5 md:py-2 text-start flex items-center justify-between md:block">
          <label className="block text-[12px] text-ink-500 font-bold mb-0.5">
            {t("عدد الضيوف", "Guests")}
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setAdults((n) => Math.max(1, n - 1))}
              className="flex h-7 w-7 items-center justify-center rounded-full border border-clay-100 text-ink-900 hover:border-clay-600 hover:text-clay-600 transition-colors"
              aria-label="decrease"
            >
              −
            </button>
            <span className="min-w-4 text-center text-[15px] font-semibold text-ink-900">
              {adults}
            </span>
            <button
              type="button"
              onClick={() => setAdults((n) => Math.min(30, n + 1))}
              className="flex h-7 w-7 items-center justify-center rounded-full border border-clay-100 text-ink-900 hover:border-clay-600 hover:text-clay-600 transition-colors"
              aria-label="increase"
            >
              +
            </button>
          </div>
        </div>
      </div>

      <button
        type="submit"
        className="bg-clay-600 text-white w-full md:w-auto h-13 md:h-full px-8 rounded-2xl md:rounded-full flex items-center justify-center gap-2 font-bold hover:bg-clay-700 transition-all shrink-0 shadow-lg"
      >
        <Icon name="search" />
        {t("بحث", "Search")}
      </button>
    </form>
  );
}
